import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Icons from "../icons";
import { statusOf } from "../testStatus";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function StatusPill({ status }) {
  const meta = statusOf(status);
  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
        meta.badge,
      ].join(" ")}
    >
      <span className={["h-1.5 w-1.5 rounded-full", meta.dot].join(" ")} />
      {meta.label}
    </span>
  );
}

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "passed", label: "Passed" },
  { value: "failed", label: "Failed" },
  { value: "running", label: "Running" },
  { value: "cancelled", label: "Stopped" },
];

export default function HistoryView({ boards = [] }) {
  const [boardId, setBoardId] = useState("");
  const [status, setStatus] = useState("");
  const [executor, setExecutor] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Size the scrollable grid to the space left in the viewport so the grid
  // itself scrolls and the page doesn't. Measured at runtime (and on resize)
  // from the grid's actual top position, so it stays correct no matter how
  // tall the filters/tabs above it are.
  const scrollRef = useRef(null);
  const [maxH, setMaxH] = useState(420);
  useLayoutEffect(() => {
    const measure = () => {
      const el = scrollRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setMaxH(Math.max(200, window.innerHeight - top - 44));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [rows.length, loading, error]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (boardId) params.set("board_id", boardId);
      if (status) params.set("status", status);
      if (executor.trim()) params.set("executor", executor.trim());
      if (dateFrom) params.set("date_from", new Date(dateFrom).toISOString());
      if (dateTo) params.set("date_to", new Date(dateTo).toISOString());
      const res = await fetch(`${API_BASE_URL}reports/history?${params.toString()}`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      setRows(Array.isArray(body?.data) ? body.data : []);
    } catch (e) {
      setError(e.message || "Failed to load history.");
    } finally {
      setLoading(false);
    }
  }, [boardId, status, executor, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const reset = () => {
    setBoardId("");
    setStatus("");
    setExecutor("");
    setDateFrom("");
    setDateTo("");
  };

  const inputCls =
    "rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-700 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25";

  return (
    <div>
      {/* Filters */}
      <div className="mb-5 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Test Suite</label>
          <select value={boardId} onChange={(e) => setBoardId(e.target.value)} className={inputCls}>
            <option value="">All test suites</option>
            {boards.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Executor</label>
          <input
            value={executor}
            onChange={(e) => setExecutor(e.target.value)}
            placeholder="name"
            className={inputCls}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={inputCls} />
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800"
        >
          <Icons.Search className="h-4 w-4" />
          Apply
        </button>
        <button
          type="button"
          onClick={reset}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
        >
          Reset
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-slate-400">
          <Icons.Spinner className="h-5 w-5 animate-spin" />
          Loading history…
        </div>
      ) : error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-card px-6 py-12 text-center text-sm text-slate-500">
          No runs match the current filters.
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-card shadow-sm">
          <div
            ref={scrollRef}
            style={{ maxHeight: maxH }}
            className="overflow-auto rounded-xl"
          >
            <table className="min-w-full text-left">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500 shadow-[inset_0_-1px_0_rgb(226_232_240)]">
                  <th className="px-4 py-3">Test case</th>
                  <th className="px-4 py-3">Test Suite</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Passed</th>
                  <th className="px-4 py-3">Failed</th>
                  <th className="px-4 py-3">Executor</th>
                  <th className="px-4 py-3">Started</th>
                  <th className="px-4 py-3">Duration</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.runkey} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-medium text-slate-800">
                      {r.testcase_name || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{r.board_id || "—"}</td>
                    <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                    <td className="px-4 py-3 text-sm text-emerald-600">{r.passed}</td>
                    <td className="px-4 py-3 text-sm text-rose-600">{r.failed || "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{r.executor || "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{fmt(r.started_at)}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {r.duration_seconds != null ? `${Math.round(r.duration_seconds)}s` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
